import { ColDef, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo } from 'react';
import { AggridInsertButton, SmallTitleWithSubSpan } from 'src/components/common/common';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
    CreateCellClickHandler,
    CustomCellClickAction,
    createCellCssClassRules,
    GridCellConfig,
    InsertRowHandler,
} from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { showWarning } from 'src/utils/toast/toastUtil';
import { defaultHRM1040_R01EducationDto, HRM1040_R01EducationDto } from './Dto_HRM1040_R01';
import { useHRM1040_R01CellChange } from './hooks/HRM1040_R01CellChange';
import { useHRM1040_R01Store } from './Store_HRM1040_R01';
import { formatHRM1040_R01Code, formatHRM1040_R01Date } from './Util_HRM1040_R01';

/** 학력정보에서 클릭·키보드 편집을 허용할 컬럼이다. */
const EDITABLE_COLUMNS = ['schoolNm', 'majorNm', 'degreeCd', 'admissionDt', 'graduateDt', 'graduateType', 'location', 'remark'];
/** 컬럼 설정 저장과 복원에 쓰는 학력 그리드 키이다. */
const COLUMN_STATE_KEY = 'HRM1040_R01-education-grid-column-state';
/** 공통 컬럼 복원 함수에 전달하는 학력 그리드 제목이다. */
const HEADER_NAMES = {
    rowIndex: '', eduSeq: '순번', schoolNm: '학교명', majorNm: '전공',
    degreeCd: '학위', admissionDt: '입학일', graduateDt: '졸업일',
    graduateType: '졸업구분', location: '소재지', remark: '비고',
};

/** 학력 그리드의 선택 목록과 버튼을 연결할 값이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1040_R01EducationDto> | null>; // 학력 그리드
}

/** 학력이 없을 때 그리드 안 안내 화면이 받는 값이다. */
interface EducationEmptyOverlayProps {
    onInsertFirst: (event: React.MouseEvent) => void; // 첫 학력 행 추가
}

/**
 * 학력이 한 건도 없을 때 그리드 안에 표시하는 안내 화면이다.
 * 행이 없으면 행 번호 칸의 +도 없으므로 첫 학력은 이 +로 추가한다. 액션바 등록은 메뉴 권한으로 끈다.
 * 그리드가 한 번 등록한 컴포넌트를 계속 쓰므로 렌더링마다 새로 만들어지지 않게 컴포넌트 밖에 둔다.
 * @param onInsertFirst +를 눌렀을 때 첫 학력 행을 추가하는 함수
 * @returns 사원을 선택했으면 안내 문구와 + 버튼, 아니면 기본 빈 목록 문구
 */
const EducationEmptyOverlay = ({ onInsertFirst }: EducationEmptyOverlayProps) => {
    // 선택 사원. 사원을 고르기 전에는 추가할 대상이 없으므로 버튼을 숨긴다.
    const selectedHRM1040_R01 = useHRM1040_R01Store(state => state.selectedHRM1040_R01);
    if (!selectedHRM1040_R01) return <span style={{ padding: 32 }}>조회된 데이터가 없습니다.</span>;
    return (
        // AG Grid 안내 영역은 클릭을 받지 않으므로 이 영역만 pointerEvents를 켠다.
        <div className="flex items-center gap-[8px]" style={{ pointerEvents: 'auto' }}>
            <span>등록된 학력이 없습니다.</span>
            {/* 행 번호 칸과 같은 공통 + 버튼 */}
            <AggridInsertButton handleInsertClick={onInsertFirst} />
        </div>
    );
};

/**
 * 선택 사원의 학력정보를 편집하는 그리드이다.
 * @param gridRef 학력 그리드 참조
 * @returns 학력 편집 그리드 JSX
 */
export const EducationTable_HRM1040_R01 = ({ gridRef }: Props) => {
    // 앱 초기화 때 읽어 둔 공통코드 중 학위 컬럼과 졸업구분 컬럼의 코드 목록을 가져온다.
    const { educationTypeList, graduateTypeList } = useCommonCodeStore();
    // 화면 Store에서 선택 사원, 학력 목록·원본, 행 교체·제거 함수, 선택·삭제 표시, 컬럼·스크롤 상태를 가져온다.
    const { selectedHRM1040_R01, HRM1040_R01EducationList, originalHRM1040_R01EducationList,
        updateHRM1040_R01EducationList, removeHRM1040_R01EducationList,
        selectedHRM1040_R01EducationList, setSelectedHRM1040_R01EducationList,
        deletedHRM1040_R01EducationList, educationColumnState, setEducationColumnState,
        educationScrollTop, setEducationScrollTop } = useHRM1040_R01Store();
    // 셀 값 변경을 학력 목록에 반영하는 함수를 가져온다.
    const { handleEducationCellChange } = useHRM1040_R01CellChange();
    // 그리드 기본 설정: 행 ID, 행 번호 렌더러, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록을 받는다.
    const {
        getRowId,
        renderIndexCell,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
    } = useGridSettingContent<HRM1040_R01EducationDto>({
        gridRef,
        key: 'valueIndex',
        gridData: HRM1040_R01EducationList,
        defaultGridData: defaultHRM1040_R01EducationDto,
        selectedRow: selectedHRM1040_R01EducationList,
        originalGridData: originalHRM1040_R01EducationList,
        updateRow: updateHRM1040_R01EducationList,
    });
    /**
     * 선택 사원의 삭제되지 않은 학력 행만 편집할 수 있다.
     * @param row 확인할 학력 행
     * @returns 편집할 수 있으면 true
     */
    const editable = useCallback(
        (row?: HRM1040_R01EducationDto) => !!row && !!selectedHRM1040_R01
            && !deletedHRM1040_R01EducationList.includes(String(row.valueIndex)),
        [selectedHRM1040_R01, deletedHRM1040_R01EducationList],
    );
    /**
     * 신규 학력 행의 -는 저장 전 행을 즉시 제거한다. 저장된 행은 액션바 삭제 기능으로 표시한다.
     * @param row -를 누른 학력 행
     */
    const handleRemoveRow = useCallback((row: HRM1040_R01EducationDto) => {
        if (!row.inserted) return;
        removeHRM1040_R01EducationList(row.valueIndex);
        setSelectedHRM1040_R01EducationList(
            selectedHRM1040_R01EducationList.filter(id => id !== String(row.valueIndex)),
        );
    }, [removeHRM1040_R01EducationList, setSelectedHRM1040_R01EducationList, selectedHRM1040_R01EducationList]);
    /**
     * 학력 행을 추가한다. index의 +는 클릭한 행 뒤에, 빈 그리드의 +는 맨 끝(첫 행)에 추가한다.
     * @param event 클릭 이벤트
     * @param rowIndex +를 누른 행의 표시 순서. 없으면 맨 끝에 추가한다.
     */
    const handleEducationInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number) => {
        const state = useHRM1040_R01Store.getState();
        // ?.: 선택 사원이 없으면 undefined가 된다.
        const empNo = state.selectedHRM1040_R01?.empNo;
        if (!empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }
        // 학력 기본 행을 복사하고 선택 사원번호와 신규 표시를 덮어쓴다.
        const newEducation = { ...defaultHRM1040_R01EducationDto, empNo, inserted: true };
        if (rowIndex === undefined) {
            state.insertHRM1040_R01EducationList(newEducation);
            return;
        }
        /** index의 +는 공통 함수를 통해 클릭한 학력 행 뒤에 삽입한다. */
        InsertRowHandler({
            event,
            dataList: state.HRM1040_R01EducationList,
            rowData: state.HRM1040_R01EducationList[rowIndex],
            defaultRowData: newEducation,
            insertRow: (rows, index) => rows.forEach(row =>
                state.insertHRM1040_R01EducationList(row, index)),
        });
    }, []);
    /**
     * 공통 index 렌더러로 행 상태와 행별 +, - 버튼을 표시한다.
     * @param params 행 번호 셀 정보
     * @returns 번호 또는 I·U·D 표시와 +, - 버튼 JSX
     */
    const renderEducationIndexCell = useCallback((params: ICellRendererParams<HRM1040_R01EducationDto>) =>
        renderIndexCell(
            params,
            // 행 상태 판단: 공통 함수가 선택·삭제·신규·수정 여부를 계산해 반환한다.
            innerParams => {
                const flags = getRowStyleFlags(innerParams, {
                    deletedList: deletedHRM1040_R01EducationList,
                    selectedList: selectedHRM1040_R01EducationList,
                    dataList: HRM1040_R01EducationList,
                });
                /** 공통 상태 함수가 받는 문자열 ID와 실제 숫자 ID를 맞춘다. */
                const rowId = String(innerParams.data?.valueIndex);
                // 공통 계산 결과를 복사하고 선택·삭제 여부만 문자열 ID 기준으로 덮어쓴다.
                return {
                    ...flags,
                    isDeleted: deletedHRM1040_R01EducationList.includes(rowId),
                    isSelected: selectedHRM1040_R01EducationList.includes(rowId),
                };
            },
            (rowIndex: number, event: React.MouseEvent) => handleEducationInsertClick(event, rowIndex),
            handleRemoveRow,
        ), [renderIndexCell, deletedHRM1040_R01EducationList, selectedHRM1040_R01EducationList,
        HRM1040_R01EducationList, handleEducationInsertClick, handleRemoveRow]);
    /** 행별 index 열과 학력 정보 열을 정의한다. */
    const baseColumnDefs = useMemo<ColDef<HRM1040_R01EducationDto>[]>(() => [
        // 공통 행 번호 컬럼 설정 배열을 풀어 넣는다.
        ...getDefaultRowIndexColumnConfig(renderEducationIndexCell),
        {
            headerName: '순번', colId: 'eduSeq', field: 'eduSeq', width: 56,
            // 신규 행의 순번(null)은 빈칸으로 표시한다.
            valueFormatter: params => params.value == null ? '' : String(params.value),
        },
        {
            headerName: '학교명', colId: 'schoolNm', field: 'schoolNm', width: 150,
            // 학교명·전공·소재지는 DB 컬럼 길이 50자까지 입력받는다.
            editable: params => editable(params.data), cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: '전공', colId: 'majorNm', field: 'majorNm', width: 110,
            editable: params => editable(params.data), cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: '학위', colId: 'degreeCd', field: 'degreeCd', width: 90,
            editable: params => editable(params.data), cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: educationTypeList.map(item => item.minorCd) },
            valueFormatter: params => formatHRM1040_R01Code(educationTypeList, String(params.value || '')),
        },
        {
            headerName: '입학일', colId: 'admissionDt', field: 'admissionDt', width: 120,
            editable: params => editable(params.data), cellDataType: 'dateString',
            cellEditor: 'agDateStringCellEditor',
            // 1900-01-01(날짜 없음)은 빈칸으로 표시한다.
            valueFormatter: params => formatHRM1040_R01Date(String(params.value || '')),
            // 날짜를 지우면 DB 기본값 1900-01-01로 저장한다.
            valueParser: params => String(params.newValue || '') || '1900-01-01',
        },
        {
            headerName: '졸업일', colId: 'graduateDt', field: 'graduateDt', width: 120,
            editable: params => editable(params.data), cellDataType: 'dateString',
            cellEditor: 'agDateStringCellEditor',
            valueFormatter: params => formatHRM1040_R01Date(String(params.value || '')),
            valueParser: params => String(params.newValue || '') || '1900-01-01',
        },
        {
            headerName: '졸업구분', colId: 'graduateType', field: 'graduateType', width: 84,
            editable: params => editable(params.data), cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: graduateTypeList.map(item => item.minorCd) },
            valueFormatter: params => formatHRM1040_R01Code(graduateTypeList, String(params.value || '')),
        },
        {
            headerName: '소재지', colId: 'location', field: 'location', width: 100,
            editable: params => editable(params.data), cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 160,
            // 비고는 DB 컬럼 길이 1000자까지 입력받는다.
            editable: params => editable(params.data), cellEditorParams: { maxLength: 1000 },
        },
    ], [educationTypeList, graduateTypeList, editable, renderEducationIndexCell]);
    // 기본 컬럼 순서. 의존성: 컬럼 정의가 바뀔 때만 다시 만든다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    /** 저장된 학력 그리드의 컬럼 배치를 공통 함수로 복원한다. */
    const columnDefs = useMemo(() => {
        if (educationColumnState === null) return baseColumnDefs;
        const restoredColumns = GridCustomColumnDefs<HRM1040_R01EducationDto>({
            baseColumnDefs,
            columnState: educationColumnState,
            headerNames: HEADER_NAMES,
        });
        /** 이전 저장 설정에 index가 없더라도 새 index 열을 맨 앞에 표시한다. */
        return restoredColumns.some(column => 'colId' in column && column.colId === 'rowIndex')
            ? restoredColumns : [baseColumnDefs[0], ...restoredColumns];
    }, [baseColumnDefs, educationColumnState]);
    /** 선택·삭제·신규 셀 표시와 편집 가능한 컬럼 표시를 공통 규칙으로 만든다. 처음 한 번만 만든다. */
    const cellCssConfig = useMemo<GridCellConfig>(() => ({
        // 모든 행에서 비활성(회색)으로 표시할 컬럼. 순번은 서버가 매긴다.
        disabledCells: ['eduSeq'],
        // 신규 행에서만 비활성으로 표시할 컬럼
        insertedDisabledCells: [],
        // 기존 행에서만 비활성으로 표시할 컬럼
        commonDisabledCells: [],
        // 모든 행에서 손가락 커서를 표시할 컬럼
        pointerCells: EDITABLE_COLUMNS,
        // 신규 행에서만 손가락 커서를 표시할 컬럼
        insertedPointerCells: [],
        // 기존 행에서만 손가락 커서를 표시할 컬럼
        commonPointerCells: [],
        // 왼쪽 정렬할 컬럼
        alignLeftCells: ['schoolNm', 'majorNm', 'location', 'remark'],
    }), []);
    // 위 설정을 신규 행용·기존 행용 컬럼 목록으로 합친 결과. 의존성: 설정 객체
    const cellCssColumns = useMemo(() => createCellCssClassRules(cellCssConfig), [cellCssConfig]);
    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙(선택·삭제·비활성·포인터·정렬)을 더한 객체
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM1040_R01EducationDto>({
            idKey: 'valueIndex',
            // 공통 셀 규칙은 숫자 valueIndex와 비교하므로 문자열 ID를 숫자로 바꿔 넘긴다.
            selectedList: selectedHRM1040_R01EducationList.map(Number),
            deletedList: deletedHRM1040_R01EducationList.map(Number),
            ...cellCssColumns,
        }),
    }), [createCellClassRules, selectedHRM1040_R01EducationList, deletedHRM1040_R01EducationList, cellCssColumns]);
    /** 공통 이벤트가 편집 가능한 학력 컬럼 사이의 키보드 이동을 처리한다. */
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM1040_R01EducationList,
        editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });
    // 셀 클릭 동작 목록: 편집 가능한 학력 행의 편집 컬럼을 클릭하면 셀 편집을 시작한다. 의존성: 편집 판단 함수
    const cellClickActions = useMemo<CustomCellClickAction<HRM1040_R01EducationDto>[]>(() => [{
        validate: (field, row) => EDITABLE_COLUMNS.includes(field) && editable(row),
        // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], [editable]);
    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(HRM1040_R01EducationList, 'valueIndex', cellClickActions);
    /**
     * Ctrl을 누르면 다중 선택을 토글하고, 일반 클릭은 해당 학력만 선택한다.
     * @param event 클릭한 행 정보
     */
    const handleRowClick = (event: RowClickedEvent<HRM1040_R01EducationDto>) => {
        if (!event.data) return;
        const rowId = String(event.data.valueIndex);
        // 원본 마우스 이벤트. Ctrl·Command 키 눌림 여부 확인에 쓴다.
        const mouseEvent = event.event as MouseEvent | undefined;
        if (!mouseEvent?.ctrlKey && !mouseEvent?.metaKey) {
            setSelectedHRM1040_R01EducationList([rowId]);
            return;
        }
        // true이면 이미 선택된 행이라 선택을 해제한다.
        const isSelected = selectedHRM1040_R01EducationList.includes(rowId);
        setSelectedHRM1040_R01EducationList(isSelected
            ? selectedHRM1040_R01EducationList.filter(id => id !== rowId)
            : [...selectedHRM1040_R01EducationList, rowId]);
    };
    /** 하단 고정 행의 순번 칸에 현재 학력 건수를 표시한다. */
    const pinnedBottomRow = useMemo(() => [{ eduSeq: `${HRM1040_R01EducationList.length}건` }], [HRM1040_R01EducationList]);
    // 선택·삭제·신규·수정 행 배경색 규칙
    const rowClassRules = createRowClassRules<HRM1040_R01EducationDto>({
        idKey: 'valueIndex',
        selectedList: selectedHRM1040_R01EducationList.map(Number),
        deletedList: deletedHRM1040_R01EducationList.map(Number),
    });
    // 제목에 표시할 "사원번호 이름". 선택 사원이 없으면 빈 문자열이다.
    const employeeName = selectedHRM1040_R01
        ? `${selectedHRM1040_R01.empNo} ${selectedHRM1040_R01.empNm}` : '';
    // 빈 그리드 안내 화면에 넘길 값. rowIndex 없이 부르면 맨 끝(빈 목록이면 첫 행)에 추가한다.
    const emptyOverlayParams = useMemo<EducationEmptyOverlayProps>(() => ({
        onInsertFirst: event => handleEducationInsertClick(event),
    }), [handleEducationInsertClick]);

    return (
        // 영역 안을 클릭하면 마지막 작업 그리드를 학력으로 기록한다. 액션바 등록·삭제·Excel 대상이 된다.
        <section className="flex flex-col h-full min-h-0"
            onClickCapture={() => useHRM1040_R01Store.getState().setActiveGridType('education')}>
            {/* 우클릭 메뉴, 전체 선택·선택 해제, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트. 전체 선택은 삭제 표시 행을 뺀다. */}
            <GridContextWrapper<HRM1040_R01EducationDto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                initSelected={() => setSelectedHRM1040_R01EducationList([])}
                allSelected={() => setSelectedHRM1040_R01EducationList(HRM1040_R01EducationList
                    .filter(row => !deletedHRM1040_R01EducationList.includes(String(row.valueIndex)))
                    .map(row => String(row.valueIndex)))}
                defaultRowData={defaultHRM1040_R01EducationDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setEducationColumnState}
                localStorageKey={COLUMN_STATE_KEY}>
                {/* 공통 제목은 학력정보를, 오른쪽 보조 문구는 선택 사원을 표시한다. */}
                <SmallTitleWithSubSpan title="학력정보" subTitle={employeeName} />
                {/* 학력 편집 그리드: 행·셀 CSS 규칙, 행 선택, 셀 클릭 편집, 키보드 이동, 값 변경, 하단 건수 행, 빈 목록 안내를 연결한다. */}
                <Grid_Excel<HRM1040_R01EducationDto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={rowClassRules}
                    onRowClicked={handleRowClick}
                    onCellClicked={handleCellClick}
                    onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                    onCellValueChanged={handleEducationCellChange}
                    noRowsOverlayComponent={EducationEmptyOverlay}
                    noRowsOverlayComponentParams={emptyOverlayParams}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setEducationColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={educationScrollTop}
                    setScrollTop={setEducationScrollTop} />
            </GridContextWrapper>
        </section>
    );
};
